import type { Router } from 'vue-router'
import { useUserStore } from '@/store/user'
import { usePermissionStore } from '@/store/permission'
import { i18n } from '@/locales'
import NProgress from 'nprogress'
import 'nprogress/nprogress.css'

// 配置 NProgress
NProgress.configure({ showSpinner: false, speed: 500 })

// 不需要登录的白名单
const whiteList = ['/official', '/home', '/login', '/register', '/forget', '/orchestrator', '/orchestrator/']

export function setupRouterGuards(router: Router) {
  // 前置守卫 - 页面跳转前执行
  router.beforeEach(async (to, from, next) => {
    // 开始进度条
    NProgress.start()
    if (to.path.startsWith('/orchestrator') || to.name === 'orchestrator') {
      next()
      return
    }

    // 设置页面标题
    const baseTitle = import.meta.env.VITE_APP_TITLE || 'Connect Ai-智能物联网管理平台'
    if (to.meta.title) {
      // 如果是翻译键，则进行翻译
      const title = to.meta.title?.startsWith('router.') 
        ? i18n.global.t(to.meta.title!)
        : to.meta.title
      document.title = `${title} - ${baseTitle}`
    } else {
      document.title = baseTitle
    }

    const userStore = useUserStore()
    const permissionStore = usePermissionStore()
    // 后端使用 Session 认证，检查 userInfo 是否存在
    const hasLogin = !!userStore.userInfo
    const isAdmin = String(userStore.userInfo?.isAdmin) === '1'

    // 1. 未登录处理
    if (!hasLogin) {
      if (to.meta.requiresAuth === false || whiteList.includes(to.path)) {
        next()
      } else {
        next(`/login?redirect=${to.path}`)
        NProgress.done()
      }
      return
    }

    // 2. 已登录处理
    if (to.path === '/login') {
      // 如果已登录，访问登录页则跳转到首页
      next({ path: '/dashboard' })
      NProgress.done()
      return
    }

    // 3. 权限检查
    if (to.meta.requiresAuth) {
      if (to.meta.requiresDeviceTypeMaintenance) {
        if (!permissionStore.permissionDataLoaded) {
          await permissionStore.fetchUserPermissions()
        }
        if (!permissionStore.canMaintainDeviceTypes) {
          console.warn(`用户无设备类型维护权限: ${to.path}`)
          next('/403')
          NProgress.done()
          return
        }
      }
      if (to.meta.permission) {
        if (!permissionStore.permissionDataLoaded) {
          await permissionStore.fetchUserPermissions()
        }
        if (!permissionStore.hasPermission(to.meta.permission)) {
          console.warn(`用户无页面权限: ${to.path}`)
          next('/403')
          NProgress.done()
          return
        }
      }
      // 检查是否需要管理员权限
      if (to.meta.isAdmin && !isAdmin) {
        console.warn(`用户无权限访问: ${to.path}`)
        next('/403')
        NProgress.done()
        return
      }
    }

    // 4. 放行
    next()
  })

  // 后置守卫 - 页面跳转后执行
  router.afterEach(() => {
    // 结束进度条
    NProgress.done()
  })

  // 错误处理
  router.onError((error) => {
    console.error('路由错误:', error)
    NProgress.done()
  })
}

// 使用示例：
// 在 main.ts 中：
// import { setupRouterGuards } from './router/guards'
// setupRouterGuards(router)
